import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const Sidebar = ({ isCollapsed, isMobileOpen, onCloseMobile }) => {
  const location = useLocation();

  return (
    <>
      <aside 
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'show' : ''}`}
        id="sidebar" 
        role="navigation" 
        aria-label="Main navigation"
      >
        <div className="sidebar-header d-flex align-items-center">
          <div className="sidebar-logo d-flex align-items-center flex-grow-1 overflow-hidden">
            <span className="logo-icon" aria-label="TriServe Energy logo">
              <i className="bi bi-lightning-charge-fill" aria-hidden="true"></i>
            </span>
            <span className="brand-label">TriServe Energy</span>
          </div>
        </div>

        <nav className="sidebar-nav" id="sidebarNav" role="navigation" aria-label="Sidebar navigation">
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/dashboard" 
            title="Dashboard" 
            data-nav-label="Dashboard"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-house" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Dashboard</span>
          </NavLink>

          <div className={`nav-section-title mt-3 ${isCollapsed ? 'visually-hidden' : ''}`} role="heading" aria-level="3">
            Customer Management
          </div>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/new-customer" 
            title="Create New Customer" 
            data-nav-label="New Customer"
            aria-label="Create New Customer - Opens customer registration form"
            end
            onClick={(e) => {
              onCloseMobile();
              // If we're already on the new-customer page, check if we need to show start over dialog
              if (location.pathname === '/new-customer' && window.triggerNewCustomerStartOver) {
                e.preventDefault();
                window.triggerNewCustomerStartOver();
              }
            }}
          >
            <span className="nav-icon">
              <i className="bi bi-person-plus" aria-hidden="true"></i>
            </span>
            <span className="nav-label">New Customer</span>
          </NavLink>

          <NavLink
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/accounts"
            title="Customers"
            data-nav-label="Customers"
            aria-label="Customers - View utility billing customer accounts from Salesforce"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-people" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Customers</span>
          </NavLink>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/move-in" 
            title="Move-In Service" 
            data-nav-label="Move-In"
            aria-label="Move-In Service - Process utility connection for new address"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-box-arrow-in-right" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Move-In</span>
          </NavLink>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/move-out" 
            title="Move-Out Service" 
            data-nav-label="Move-Out"
            aria-label="Move-Out Service - Process utility disconnection for address"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-box-arrow-left" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Move-Out</span>
          </NavLink>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/change-address" 
            title="Change Address" 
            data-nav-label="Change Address"
            aria-label="Change Address - Update customer service address"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-geo-alt" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Change Address</span>
          </NavLink>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/billing" 
            title="Billing" 
            data-nav-label="Billing"
            aria-label="Billing - Process customer payment transactions"
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-credit-card" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Billing</span>
          </NavLink>

          <div className={`nav-section-title mt-3 ${isCollapsed ? 'visually-hidden' : ''}`} role="heading" aria-level="3">
            OPERATIONS
          </div>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/service-requests" 
            title="Service Requests" 
            data-nav-label="Service Requests"
            aria-label="Service Requests - Manage utility service requests and work orders"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-tools" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Service Requests</span>
          </NavLink>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/work-orders" 
            title="Work Orders" 
            data-nav-label="Work Orders"
            aria-label="Work Orders - Manage field service and maintenance tasks"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-clipboard-check" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Work Orders</span>
          </NavLink>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/outage-map" 
            title="Outage Map" 
            data-nav-label="Outage Map"
            aria-label="Outage Map - View current power outages and restoration status"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Outage Map</span>
          </NavLink>

          <div className={`nav-section-title mt-3 ${isCollapsed ? 'visually-hidden' : ''}`} role="heading" aria-level="3">
            SYSTEM
          </div>
          
          <NavLink 
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            to="/settings" 
            title="Settings" 
            data-nav-label="Settings"
            aria-label="Settings - Configure dashboard preferences and user settings"
            end
            onClick={onCloseMobile}
          >
            <span className="nav-icon">
              <i className="bi bi-gear" aria-hidden="true"></i>
            </span>
            <span className="nav-label">Settings</span>
          </NavLink>
        </nav>
      </aside>

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div 
          className="sidebar-backdrop show d-lg-none" 
          onClick={onCloseMobile}
        ></div>
      )}
    </>
  );
};

export default Sidebar;
